import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "../../DynamicComponents/SnackbarContext";
import apiEndpoints from "../../apiconfig";
import WhatsAppPreview from "../whatsapppreview";
import {
  Box,
  Snackbar,
  snackbarClasses,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import {
  FaUpload,
  FaTimes,
  FaFileImage,
  FaFileVideo,
  FaFilePdf,
  FaFileAudio,
} from "react-icons/fa";
import { SupervisorAccount } from "@mui/icons-material";
import { HelpCircle } from "lucide-react";

const TEMPLATE_BODY_MAX_LENGTH = 1024;

const CreateTemplate = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const viewMode = location.state?.view || false;
  const isReadOnly = viewMode;
  const templateId = location.state?.id || null;

  const [templateBody, setTemplateBody] = useState("");
  const [templateFooter, setTemplateFooter] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showAddAttributePopup, setShowAddAttributePopup] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [attributes, setAttributes] = useState([]);
  const [newAttribute, setNewAttribute] = useState({
    name: "",
    value: "",
    type: "name",
  });
  const [quickReplies, setQuickReplies] = useState([]);
  const [newQuickReply, setNewQuickReply] = useState("");
  const [newQuickReplyAnswer, setNewQuickReplyAnswer] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [templateButtons, setTemplateButtons] = useState([]);
  const [newButton, setNewButton] = useState({
    type: "URL",
    text: "",
    url: "",
    phone: "",
  });
  const [placeholderValues, setPlaceholderValues] = useState([]);
  const [variableMeta, setVariableMeta] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaType, setMediaType] = useState("");
  const [mediaId, setMediaId] = useState(null);
  const [sampleErrors, setSampleErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [fetchedTemplate, setFetchedTemplate] = useState(null);
  const [otpButtonText, setOtpButtonText] = useState("Copy Code");

  const [formData, setFormData] = useState({
    templateName: "",
    languageGuid: "",
    type: "TEXT",
    templateCategory: "UTILITY",
    headerType: "text",
    headerText: "",
  });

  const [errors, setErrors] = useState({});

  // 🔥 Detect duplicate template data (edit/duplicate flow)
  useEffect(() => {
    if (!location.state?.template || languages.length === 0) return;

    const t = location.state.template;

    const templateLang =
      typeof t.language === "string" ? t.language : t.language?.code || "";

    const matchedLang = languages.find(
      (l) => l.code.toLowerCase() === templateLang.toLowerCase()
    );

    setFormData((prev) => ({
      ...prev,
      templateName: t.templateName || "",
      languageGuid: matchedLang?.guid || "",
      templateCategory: t.category || "UTILITY",
      type: t.type === "MEDIA" ? "MEDIA" : "TEXT",
      headerType: "text",
      headerText: "",
    }));

    setTemplateBody(t.templateBody || "");
    setTemplateFooter(t.templateFooter || "");

    if (t.attributes) {
      setPlaceholderValues(t.attributes.map((a) => a.value));
      setVariableMeta(
        t.attributes.map((a, i) => ({
          placeholder: `{{${i + 1}}}`,
          sample: a.value,
          type: "text",
        }))
      );
    }
  }, [location.state, languages]);

  const CATEGORY_RULES = {
    UTILITY: {
      allowMedia: false,
      allowButtons: true,
      allowQuickReplies: true,
      allowedButtonTypes: ["URL", "PHONE_NUMBER", "QUICK_REPLY"],
      allowHeader: true,
      allowFooter: true,
      mustHaveOTPButton: false,
    },
    AUTHENTICATION: {
      allowMedia: false,
      allowButtons: false,
      allowQuickReplies: false,
      allowedButtonTypes: [],
      allowHeader: false,
      allowFooter: false,
      mustHaveOTPButton: true,
    },
    MARKETING: {
      allowMedia: true,
      allowButtons: true,
      allowQuickReplies: true,
      allowedButtonTypes: ["URL", "PHONE_NUMBER", "QUICK_REPLY"],
      allowHeader: true,
      allowFooter: true,
      mustHaveOTPButton: false,
    },
  };

  const GLOBAL_ATTRIBUTES = [
    { name: "name", value: "user name" },
    { name: "date", value: "meet date" },
  ];

  // Fetch template details in view mode
  useEffect(() => {
    const fetchTemplateDetails = async () => {
      if (!viewMode || !templateId) return;

      try {
        const res = await fetch(
          `${apiEndpoints.managetemplate}?templateId=${templateId}`,
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          }
        );
        const data = await res.json();

        if (data.status === "success" && data.data) {
          const t = data.data;
          const headerComp = t.components?.find((c) => c.type === "HEADER");
          const bodyComp = t.components?.find((c) => c.type === "BODY");
          const footerComp = t.components?.find((c) => c.type === "FOOTER");
          const buttonsComp = t.components?.find((c) => c.type === "BUTTONS");

          const matchedLang = languages.find(
            (l) =>
              l.code.toLowerCase() === (t.language || "").toLowerCase() ||
              l.name.toLowerCase() === (t.language_name || "").toLowerCase()
          );

          setFormData({
            templateName: t.name || "",
            templateCategory: t.category || "UTILITY",
            languageGuid: matchedLang?.guid || "",
            languageName:
              matchedLang?.name || t.language_name || t.language || "English",
            type: headerComp?.format ? "MEDIA" : "TEXT",
            headerType: (headerComp?.format || "text").toLowerCase(),
            headerText: headerComp?.text || "",
          });

          setTemplateBody(bodyComp?.text || "");
          setTemplateFooter(footerComp?.text || "");

          if (bodyComp?.example?.body_text?.[0]) {
            const samples = bodyComp.example.body_text[0];
            setPlaceholderValues(samples);
            setVariableMeta(
              samples.map((s, i) => ({
                placeholder: `{{${i + 1}}}`,
                sample: s,
                type: "text",
              }))
            );
          }

          if (buttonsComp?.buttons?.length) {
            setTemplateButtons(
              buttonsComp.buttons.map((b) => ({
                type: b.type,
                text: b.text,
                url: b.url || "",
                phone: b.phone_number || "",
              }))
            );
          }

          let reconstructedFile = null;

          if (t.media?.base64 && t.media?.mime_type) {
            reconstructedFile = base64ToFile(
              t.media.base64,
              t.media.mime_type,
              `template_${t.name || "media"}`
            );

            setMediaFile(reconstructedFile);
            setMediaType(t.media.media_type);
          }

          setFetchedTemplate({
            headerType: headerComp?.format?.toLowerCase() || "text",
            headerText: headerComp?.text || "",
            body: bodyComp?.text || "",
            footer: footerComp?.text || "",
            quickReplies: [],
            templateButtons:
              buttonsComp?.buttons?.map((b) => ({
                type: b.type,
                text: b.text,
                url: b.url || "",
                phone: b.phone_number || "",
              })) || [],
            mediaType: t.media?.media_type || "",
            mediaFile: reconstructedFile,
            mediaUrl: t.media
              ? `data:${t.media.mime_type};base64,${t.media.base64}`
              : "",
          });

          showSnackbar(`Template "${t.name}" loaded successfully!`, "success");
        }
      } catch (err) {
        console.error("Fetch template details error:", err);
        showSnackbar("Error fetching template details", "error");
      }
    };

    fetchTemplateDetails();
  }, [viewMode, templateId, languages]);

  // Fetch categories and languages
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(apiEndpoints.Category);
        const data = await res.json();
        if (res.ok && data.status) {
          setCategories(data.data);
        }
      } catch (err) {
        console.error("Error loading categories:", err);
      }
    };

    const fetchLanguages = async () => {
      try {
        const res = await fetch(apiEndpoints.language);
        const data = await res.json();
        if (res.ok && data.status) {
          setLanguages(
            data.data.map((lang) => ({
              guid: lang.guid,
              name: lang.languageName,
              code: lang.code,
            }))
          );
        }
      } catch (err) {
        console.error("Error loading languages:", err);
      }
    };

    fetchCategories();
    fetchLanguages();
  }, []);

  // Enforce category rules when category changes
  useEffect(() => {
    const rules =
      CATEGORY_RULES[formData.templateCategory] || CATEGORY_RULES.UTILITY;

    if (!rules.allowMedia && formData.type === "MEDIA") {
      setFormData((prev) => ({ ...prev, type: "TEXT" }));
      setMediaFile(null);
      setMediaId(null);
      setMediaType("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    }

    if (!rules.allowHeader) {
      setFormData((prev) => ({ ...prev, headerType: "text", headerText: "" }));
    }
    if (!rules.allowFooter) {
      setTemplateFooter("");
    }
    if (!rules.allowButtons) {
      setTemplateButtons([]);
    }
    if (!rules.allowQuickReplies) {
      setQuickReplies([]);
    }

    // 🔄 UPDATED: Do NOT override body text for AUTHENTICATION.
    // Just clear header/footer as they are not allowed.
    if (formData.templateCategory === "AUTHENTICATION") {
      setFormData((prev) => ({
        ...prev,
        headerText: "",
        headerType: "text",
      }));
      setTemplateFooter("");
    }
  }, [formData.templateCategory]);

  // Auto-detect media type
  useEffect(() => {
    if (mediaFile) {
      const fileType = mediaFile.type.split("/")[0];
      if (fileType === "image") setMediaType("image");
      else if (fileType === "video") setMediaType("video");
      else if (mediaFile.type === "application/pdf") setMediaType("document");
      else if (fileType === "audio") setMediaType("audio");
      else setMediaType("document");
    }
  }, [mediaFile]);

  const handleAddAttribute = () => {
    setNewAttribute({ name: "", value: "", type: "name" });
    setShowAddAttributePopup(true);
  };

  const handleCloseAddPopup = () => setShowAddAttributePopup(false);

  const handleSaveAttribute = () => {
    const name = (newAttribute.name || "").trim();
    if (!name) {
      showSnackbar("Attribute name required", "error");
      return;
    }
    if (attributes.some((a) => a.name === name)) {
      showSnackbar("Attribute name already exists", "error");
      return;
    }

    setAttributes((prev) => [
      ...prev,
      {
        name,
        value: newAttribute.value || "",
        type: newAttribute.type || "name",
      },
    ]);
    setNewAttribute({ name: "", value: "", type: "name" });
    setShowAddAttributePopup(false);
  };

  const insertPlaceholderValue = (arg) => {
    let attrObj = null;
    if (typeof arg === "number") {
      attrObj = attributes[arg] ?? { name: "", value: "", type: "text" };
    } else if (
      typeof arg === "object" &&
      arg !== null &&
      arg.hasOwnProperty("name")
    ) {
      attrObj = arg;
    } else if (typeof arg === "string") {
      attrObj = { name: "", value: arg, type: "name" };
    } else {
      attrObj = { name: "", value: `Sample`, type: "text" };
    }

    if (
      formData.templateCategory === "AUTHENTICATION" &&
      placeholderValues.length >= 1
    ) {
      showSnackbar(
        "Authentication templates allow only one placeholder ({{1}})",
        "error"
      );
      return;
    }

    const placeholderNumber = placeholderValues.length + 1;
    const placeholder = `{{${placeholderNumber}}}`;

    if (textareaRef.current) {
      const el = textareaRef.current;
      const start = el.selectionStart || 0;
      const end = el.selectionEnd || 0;
      const before = templateBody.slice(0, start);
      const after = templateBody.slice(end);
      const newBody = before + placeholder + after;

      if (newBody.length > TEMPLATE_BODY_MAX_LENGTH) {
        showSnackbar(
          `Message body cannot exceed ${TEMPLATE_BODY_MAX_LENGTH} characters.`,
          "error"
        );
        return;
      }

      setTemplateBody(newBody);

      setTimeout(() => {
        el.focus();
        const pos = start + placeholder.length;
        el.selectionStart = el.selectionEnd = pos;
      }, 0);
    } else {
      if ((templateBody + " " + placeholder).trim().length > TEMPLATE_BODY_MAX_LENGTH) {
        showSnackbar(
          `Message body cannot exceed ${TEMPLATE_BODY_MAX_LENGTH} characters.`,
          "error"
        );
        return;
      }

      setTemplateBody((prev) =>
        prev ? prev + " " + placeholder : placeholder
      );
    }

    const sample = attrObj?.value || `Sample ${placeholderNumber}`;
    setPlaceholderValues((prev) => [...prev, sample]);
    setVariableMeta((prev = []) => [
      ...prev,
      {
        placeholder,
        name: attrObj?.name || "",
        sample,
        type: attrObj?.type || "text",
      },
    ]);
  };

  const handleBodyChange = (text) => {
    if (text.length > TEMPLATE_BODY_MAX_LENGTH) {
      showSnackbar(
        `Message body cannot exceed ${TEMPLATE_BODY_MAX_LENGTH} characters.`,
        "error"
      );
      return;
    }

    const nonNumericRegex = /\{\{\s*([^\d\}][^}]*)\s*\}\}/g;
    let metaAdded = [];
    let newText = text.replace(nonNumericRegex, (_, inner) => {
      const placeholderNumber = placeholderValues.length + metaAdded.length + 1;
      const placeholder = `{{${placeholderNumber}}}`;
      metaAdded.push({
        placeholder,
        sample: inner.trim(),
        name: "",
        type: "text",
      });
      return placeholder;
    });

    const startsWithVariable = /^\s*\{\{\d+\}\}/.test(newText);
    const endsWithVariable = /\{\{\d+\}\}\s*$/.test(newText);
    if (startsWithVariable || endsWithVariable) {
      showSnackbar(
        startsWithVariable
          ? "Variable cannot be at the start of the message body."
          : "Variable cannot be at the end of the message body.",
        "warning"
      );
    }

    setTemplateBody(newText);

    if (metaAdded.length) {
      setPlaceholderValues((prev) => [
        ...prev,
        ...metaAdded.map((m) => m.sample),
      ]);
      setVariableMeta((prev = []) => [...prev, ...metaAdded]);
      showSnackbar(
        "Converted named placeholders to numeric placeholders.",
        "info"
      );
    }
  };

  useEffect(() => {
    const matches = templateBody.match(/\{\{\d+\}\}/g) || [];
    const uniquePlaceholders = [...new Set(matches)];

    setPlaceholderValues((prev) => {
      const next = prev.slice(0, uniquePlaceholders.length);
      while (next.length < uniquePlaceholders.length) next.push("");
      return next;
    });

    setVariableMeta((prev) => {
      const next = prev.slice(0, uniquePlaceholders.length);
      while (next.length < uniquePlaceholders.length) {
        const index = next.length + 1;
        next.push({
          placeholder: `{{${index}}`,
          name: "",
          sample: "",
          type: "text",
        });
      }
      return next.map((m, i) => ({ ...m, placeholder: `{{${i + 1}}}` }));
    });
  }, [templateBody]);

  const getPreviewBody = () => {
    if (!templateBody) return "";
    return templateBody.replace(/\{\{(\d+)\}\}/g, (match, g1) => {
      const idx = parseInt(g1, 10) - 1;
      const sample = placeholderValues[idx];
      if (
        sample !== undefined &&
        sample !== null &&
        String(sample).trim() !== ""
      ) {
        return String(sample);
      }
      return match;
    });
  };

  const handleDelete = (index) => {
    const attr = attributes[index];
    const used = variableMeta.some(
      (v) => v.name === attr?.name || v.sample === attr?.value
    );
    if (used) {
      showSnackbar(
        "This attribute is used in the template. Remove placeholder from body first.",
        "error"
      );
      return;
    }
    setAttributes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddQuickReply = () => {
    if (newQuickReply.trim() && quickReplies.length < 3) {
      setQuickReplies([
        ...quickReplies,
        { text: newQuickReply.trim(), answer: newQuickReplyAnswer.trim() },
      ]);
      setNewQuickReply("");
      setNewQuickReplyAnswer("");
    }
  };

  const handleRemoveQuickReply = (index) => {
    setQuickReplies(quickReplies.filter((_, i) => i !== index));
  };

  const handleAddButton = () => {
    if (templateButtons.length < 3) {
      const buttonData = { type: newButton.type, text: newButton.text.trim() };
      if (newButton.type === "URL") buttonData.url = newButton.url;
      else if (newButton.type === "PHONE_NUMBER")
        buttonData.phone = newButton.phone;

      setTemplateButtons([...templateButtons, buttonData]);
      setNewButton({ type: "URL", text: "", url: "", phone: "" });
    }
  };

  const handleRemoveButton = (index) => {
    setTemplateButtons(templateButtons.filter((_, i) => i !== index));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const uploadMediaToServer = async (file) => {
    try {
      setIsUploadingMedia(true);
      const form = new FormData();
      form.append("media_file", file);
      console.log("Uploading media file:", file.name, file.size, file.type);
      const response = await fetch(apiEndpoints.managetemplate, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        body: form,
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Media upload failed");

      return data.handler;
    } catch (error) {
      console.error("Media upload error:", error);
      throw error;
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const submitTemplateToServer = async (templateData) => {
    try {
      const response = await fetch(apiEndpoints.managetemplate, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify(templateData),
      });

      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Template submission failed");
      return data;
    } catch (error) {
      console.error("Template submission error:", error);
      throw error;
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showSnackbar("File size must be less than 10MB", "error");
      return;
    }

    try {
      setMediaFile(file);
      showSnackbar("Uploading media file...", "info");

      const uploadedMediaId = await uploadMediaToServer(file);
      setMediaId(uploadedMediaId);
      showSnackbar("Media uploaded successfully!", "success");
    } catch (error) {
      console.error("Media upload failed:", error);
      showSnackbar("Media upload failed: " + error.message, error);
      setMediaFile(null);
      setMediaId(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeMediaFile = () => {
    setMediaFile(null);
    setMediaType("");
    setMediaId(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getMediaIcon = () => {
    switch (mediaType) {
      case "image":
        return <FaFileImage className="text-green-500 text-xl" />;
      case "video":
        return <FaFileVideo className="text-red-500 text-xl" />;
      case "document":
        return <FaFilePdf className="text-red-500 text-xl" />;
      case "audio":
        return <FaFileAudio className="text-purple-500 text-xl" />;
      default:
        return <FaFileImage className="text-green-500 text-xl" />;
    }
  };

  const validateSamplesBeforeSubmit = () => {
    const placeholderMatches = (templateBody || "").match(/\{\{\d+\}\}/g) || [];
    const uniquePlaceholders = [...new Set(placeholderMatches)];
    const errs = {};

    for (let i = 0; i < uniquePlaceholders.length; i++) {
      const sample = (placeholderValues[i] || "").toString().trim();
      if (!sample)
        errs[i] = `Please enter sample content for ${uniquePlaceholders[i]}`;
      else if (sample.length > 512)
        errs[i] = `Sample too long for ${uniquePlaceholders[i]}`;
    }

    setSampleErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateWhatsAppTemplate = () => {
    const nameRegex = /^[a-z0-9_]+$/;
    if (!formData.templateName || !nameRegex.test(formData.templateName)) {
      throw new Error(
        "Template name must contain only lowercase letters, numbers, and underscores."
      );
    }

    const body = (templateBody || "").trim();
    if (!body) throw new Error("Template body cannot be empty.");
    if (body.length > TEMPLATE_BODY_MAX_LENGTH)
      throw new Error(
        `Template body exceeds ${TEMPLATE_BODY_MAX_LENGTH} characters.`
      );

    const placeholders = [...new Set(body.match(/\{\{\d+\}\}/g) || [])];
    if (
      placeholders.length > 0 &&
      placeholderValues.length < placeholders.length
    ) {
      throw new Error("Please provide sample text for all placeholders.");
    }

    if (formData.templateCategory === "AUTHENTICATION") {
      if (placeholders.length !== 1 || placeholders[0] !== "{{1}}") {
        throw new Error(
          "Authentication templates must contain exactly one variable {{1}}."
        );
      }
      if (formData.headerText || templateFooter) {
        throw new Error(
          "Authentication templates cannot have headers or footers."
        );
      }
    }

    if (formData.headerText && formData.headerText.length > 60)
      throw new Error("Header text cannot exceed 60 characters.");
    if (templateFooter && templateFooter.length > 60)
      throw new Error("Footer text cannot exceed 60 characters.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const newErrors = {};
    if (!formData.templateName.trim())
      newErrors.templateName = "Template name is required.";
    if (!formData.languageGuid)
      newErrors.languageGuid = "Please select a language.";
    if (!formData.templateCategory)
      newErrors.templateCategory = "Please select a template category.";
    if (!formData.type) newErrors.type = "Template type is required.";
    if (!templateBody.trim())
      newErrors.templateBody = "Template body cannot be empty.";
    else if (templateBody.length > TEMPLATE_BODY_MAX_LENGTH)
      newErrors.templateBody = `Template body cannot exceed ${TEMPLATE_BODY_MAX_LENGTH} characters.`;

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    let sanitized = (formData.templateName || "").trim().toLowerCase();
    sanitized = sanitized
      .replace(/[^a-z0-9_]+/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");

    if (!sanitized) {
      showSnackbar(
        "Template name is required and must contain letters or numbers.",
        "error"
      );
      return;
    }

    const nameRegex = /^[a-z0-9_]+$/;
    if (!nameRegex.test(sanitized)) {
      showSnackbar(
        "Invalid template name. Use only lowercase letters, numbers, and underscores.",
        "error"
      );
      return;
    }

    const rules =
      CATEGORY_RULES[formData.templateCategory] || CATEGORY_RULES.UTILITY;
    if (formData.type === "MEDIA" && !rules.allowMedia) {
      showSnackbar(
        `${formData.templateCategory} templates cannot include media`,
        "error"
      );
      return;
    }

    const requiredFields = [
      "templateName",
      "templateCategory",
      "languageGuid",
      "type",
    ];
    const missing = requiredFields.filter((field) => !formData[field]);
    if (missing.length) {
      showSnackbar(`Please fill: ${missing.join(", ")}`, "error");
      return;
    }

    const isMediaTemplate = formData.type === "MEDIA" || ["image", "video", "document"].includes(formData.headerType);
    if (isMediaTemplate && !mediaId) {
      showSnackbar("Please upload a media file first", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      if (formData.templateCategory === "AUTHENTICATION") {
        let bodyText = (templateBody || "").trim();
        let body = bodyText;

        if (!/\{\{\d+\}\}/.test(body)) {
          body = `${body} {{1}}`;
        }

        body = body.replace(/\{\{\d+\}\}/g, "{{1}}");

        bodyText = body;
        setTemplateBody(body);

        setTemplateButtons([{ type: "OTP", otp_type: "COPY_CODE" }]);

        setTemplateButtons([{
          type: "OTP",
          otp_type: "COPY_CODE",
          text: otpButtonText.trim() || "Copy Code" // Fallback to default
        }]);

        setFormData((prev) => ({
          ...prev,
          headerText: "",
          headerType: "text",
        }));
        setTemplateFooter("");
      }


      if (!validateSamplesBeforeSubmit()) {
        showSnackbar("Please fill sample text for all variables.", "error");
        setIsSubmitting(false);
        return;
      }

      validateWhatsAppTemplate();
      const selectedLang = languages.find(
        (l) => l.guid === formData.languageGuid
      );
      const languageCode = selectedLang?.code || "en";

      let bodyText = (templateBody || "").trim();

      // 🔄 UPDATED AUTH body building – no English defaults
      if (formData.templateCategory === "AUTHENTICATION") {
        if (!/\{\{\d+\}\}/.test(bodyText)) {
          bodyText = `${bodyText} {{1}}`;
        }
        bodyText = bodyText.replace(/\{\{\d+\}\}/g, "{{1}}");
      }

      const components = [];
      const currentRules =
        CATEGORY_RULES[formData.templateCategory] || CATEGORY_RULES.UTILITY;

      // HEADER
      const isMediaTemplate = formData.type === "MEDIA" || ["image", "video", "document"].includes(formData.headerType);
      if (
        currentRules.allowHeader &&
        formData.type === "TEXT" &&
        !isMediaTemplate &&
        formData.headerText.trim()
      ) {
        components.push({
          type: "HEADER",
          format: formData.headerType
            ? formData.headerType.toUpperCase()
            : "TEXT",
          text: formData.headerText.trim(),
        });
      } else if (isMediaTemplate && mediaId) {
        components.push({
          type: "HEADER",
          format: mediaType ? mediaType.toUpperCase() : "DOCUMENT",
          example: {
            header_handle: [mediaId],
          },
        });
      }

      // BODY
      components.push({ type: "BODY", text: bodyText });

      // FOOTER
      if (currentRules.allowFooter && templateFooter.trim()) {
        components.push({ type: "FOOTER", text: templateFooter.trim() });
      }

      // BUTTONS
      if (currentRules.allowButtons && templateButtons.length > 0) {
        components.push({
          type: "BUTTONS",
          buttons: templateButtons.map((b) => ({
            type: b.type,
            text: b.text,
            ...(b.url ? { url: b.url } : {}),
            ...(b.phone ? { phone: b.phone } : {}),
          })),
        });
      }

      const templateData = {
        name: sanitized,
        language: languageCode,
        category: formData.templateCategory,
        templateCategory: formData.templateCategory,
        languageGuid: formData.languageGuid,
        languageCode: languageCode,
        typeId: (formData.type === "TEXT" && !["image", "video", "document"].includes(formData.headerType)) ? 1 : 2,
        isFile: (formData.type === "MEDIA" || ["image", "video", "document"].includes(formData.headerType)) ? 1 : 0,
        body: bodyText,
        templateFooter: templateFooter || "",
        templateHeaders: JSON.stringify({
          headerType: formData.type === "MEDIA" ? mediaType : formData.headerType,
          headerText: formData.headerText,
        }),
        quickReplies: JSON.stringify(quickReplies),
        templateButtons: JSON.stringify(templateButtons),
        variableSamples: JSON.stringify(
          variableMeta.length
            ? variableMeta.map((m, i) => ({
              placeholder: m.placeholder || `{{${i + 1}}}`,
              name: m.name || "",
              sample: m.sample || placeholderValues[i] || "",
              type: m.type || "text",
            }))
            : placeholderValues.map((v, i) => ({
              placeholder: `{{${i + 1}}}`,
              name: "",
              sample: v,
              type: "text",
            }))
        ),
        media_id: mediaId,
        components: components,
        ...(formData.templateCategory === "AUTHENTICATION" && {
          otpButtonText: otpButtonText.trim() || "Copy Code"
        })
      };

      const result = await submitTemplateToServer(templateData);
      showSnackbar("Template created successfully!", "success");

      if (result.warning) showSnackbar(result.warning, "warning");
      if (result.data?.meta_status === "FAILED")
        showSnackbar(
          "Template saved locally but Meta submission failed",
          "error"
        );

      navigate("/templates");
    } catch (error) {
      console.error("Template submission error:", error);
      showSnackbar(error.message || "Failed to create template", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentRules =
    CATEGORY_RULES[formData.templateCategory] || CATEGORY_RULES.UTILITY;
  const showHeader = currentRules.allowHeader && formData.type === "TEXT";
  const showFooter = currentRules.allowFooter;
  const showButtons = currentRules.allowButtons;
  const showMediaSection =
    currentRules.allowMedia &&
    (formData.type === "MEDIA" ||
      ["image", "video", "document"].includes(formData.headerType));
  const showQuickReplies = currentRules.allowQuickReplies;

  const previewData =
    viewMode && fetchedTemplate
      ? {
        headerType: fetchedTemplate.headerType,
        headerText: fetchedTemplate.headerText,
        body: fetchedTemplate.body,
        footer: fetchedTemplate.footer,
        quickReplies: fetchedTemplate.quickReplies,
        templateButtons: fetchedTemplate.templateButtons,
        mediaFile: fetchedTemplate.mediaFile,
        mediaType: fetchedTemplate.mediaType,
        mediaUrl: fetchedTemplate.mediaUrl,
        fileName: fetchedTemplate.fileName || "",
        templateType: formData.type,
      }
      : {
        headerType: formData.headerType,
        headerText: formData.headerText,
        body: getPreviewBody(),
        footer: templateFooter,
        quickReplies: quickReplies,
        templateButtons: templateButtons,
        mediaFile: mediaFile,
        mediaType: mediaType,
        mediaUrl: mediaFile ? URL.createObjectURL(mediaFile) : null,
        fileName: mediaFile ? mediaFile.name : "",
        templateType: formData.type,
      };

  const InfoIcon = ({ text }) => (
    <Tooltip title={text}>
      <HelpCircle
        size={16}
        className="inline-block ml-1 text-slate-400 hover:text-[#25D366] cursor-help"
      />
    </Tooltip>
  );

const base64ToFile = (base64, mimeType, fileName = "media") => {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);

  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  return new File([ab], fileName, { type: mimeType });
};


  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50 p-4 md:p-6 font-['Montserrat']">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                {viewMode ? "Template Details" : "Create New Template"}
              </h1>
              <p className="text-gray-600">
                {viewMode
                  ? "View template details and configuration"
                  : "Design and configure WhatsApp Business templates"}
              </p>
            </div>
            <button
              onClick={() => navigate("/templates")}
              className="mt-4 md:mt-0 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 font-medium"
            >
              Back to Templates
            </button>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
          {/* Form Section */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
            <div className="p-6">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Template Configuration
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  Configure your template settings and content
                </p>
              </div>

              <div className="space-y-8">
                {/* Basic Information */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1 flex items-center">
                        Template Name
                        <InfoIcon text="Name your template using lowercase letters, numbers, and underscores only (e.g., order_update)." />
                      </label>
                      <input
                        type="text"
                        name="templateName"
                        className={`w-full rounded-lg p-2 border ${
                          errors.templateName
                            ? "border-red-500"
                            : "border-slate-300"
                        } focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/40 outline-none transition-all text-sm md:text-base`}
                        value={formData.templateName}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      />
                      {errors.templateName && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.templateName}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1 flex items-center">
                        Language
                        <InfoIcon text="Choose a Meta-supported language. Template text and variables must match this language." />
                      </label>
                      <select
                        name="languageGuid"
                        className={`w-full rounded-lg p-2 border ${
                          errors.languageGuid
                            ? "border-red-500"
                            : "border-slate-300"
                        } focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/40 outline-none transition-all text-sm md:text-base`}
                        value={formData.languageGuid}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="">Select language</option>
                        {languages.map((lang) => (
                          <option key={lang.guid} value={lang.guid}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                      {errors.languageGuid && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.languageGuid}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1 flex items-center">
                        Template Type
                        <InfoIcon text="Select 'Text' for message-only templates or 'Media' if your template includes an image, video, or document header." />
                      </label>
                      <select
                        name="type"
                        className={`w-full rounded-lg p-2 border ${
                          errors.type ? "border-red-500" : "border-slate-300"
                        } focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/40 outline-none transition-all text-sm md:text-base`}
                        value={formData.type}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="TEXT">Text</option>
                        <option
                          value="MEDIA"
                          disabled={!currentRules.allowMedia}
                        >
                          Media
                        </option>
                      </select>
                      {errors.type && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.type}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1 flex items-center">
                        Template Category
                        <InfoIcon text="Utility: for updates or confirmations; Marketing: for promotions; Authentication: for login or OTP codes." />
                      </label>
                      <select
                        name="templateCategory"
                        className={`w-full rounded-lg p-2 border ${
                          errors.templateCategory
                            ? "border-red-500"
                            : "border-slate-300"
                        } focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/40 outline-none transition-all text-sm md:text-base`}
                        value={formData.templateCategory}
                        onChange={handleChange}
                        disabled={isReadOnly}
                      >
                        <option value="">Select category</option>
                        <option value="UTILITY">Utility</option>
                        <option value="AUTHENTICATION">Authentication</option>
                        <option value="MARKETING">Marketing</option>
                      </select>
                      {errors.templateCategory && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.templateCategory}
                        </p>
                      )}
                    </div>
                  </div>
                </section>

                {/* Media Section */}
                {showMediaSection && (
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Media Content
                    </h3>
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 bg-gray-50">
                      {!mediaFile ? (
                        <div className="text-center">
                          <FaUpload className="mx-auto text-3xl text-gray-400 mb-4" />
                          <p className="text-gray-600 mb-2">
                            Upload media file
                          </p>
                          <p className="text-sm text-gray-500 mb-4">
                            Supports images, videos, documents, audio (Max 10MB)
                          </p>
                          <label className="inline-flex items-center px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl cursor-pointer transition-colors">
                            <FaUpload className="mr-2" />
                            Choose File
                            <input
                              ref={fileInputRef}
                              type="file"
                              onChange={handleMediaUpload}
                              accept="image/*,video/*,.pdf,.doc,.docx,.txt,audio/*"
                              className="hidden"
                            />
                            <InfoIcon text="Upload an image, video, document, or audio file (max 10MB). Required for Media templates." />
                          </label>
                        </div>
                      ) : (
                        <div className="bg-white rounded-xl p-4 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {getMediaIcon()}
                              <div>
                                <p className="font-medium text-gray-900">
                                  {mediaFile.name}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {(mediaFile.size / (1024 * 1024)).toFixed(2)}{" "}
                                  MB • {mediaType}
                                </p>
                                {/* {mediaId && (
                                    <p className="text-xs text-green-600 font-mono">
                                      Media ID: {mediaId}
                                    </p>
                                  )} */}
                              </div>
                            </div>
                            <button
                              onClick={removeMediaFile}
                              disabled={isReadOnly}
                              className={`p-2 rounded-lg transition-colors ${
                                isReadOnly
                                  ? "cursor-not-allowed text-gray-300"
                                  : "hover:bg-gray-100 text-gray-500 hover:text-red-500"
                              }`}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Header Section */}
                {showHeader && (
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Header Configuration
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Header Type
                          <InfoIcon text="Optional. Choose Text or Media. Media headers are only allowed for Marketing templates." />
                        </label>
                        <select
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                          name="headerType"
                          value={formData.headerType}
                          onChange={(e) => {
                            handleChange(e);
                            if (
                              ["image", "video", "document"].includes(
                                e.target.value
                              )
                            ) {
                              setFormData((prev) => ({
                                ...prev,
                                headerText: "",
                              }));
                            }
                          }}
                          disabled={isReadOnly}
                        >
                          <option value="text">Text</option>
                          {formData.templateCategory === "MARKETING" && (
                            <>
                              <option value="image">Image</option>
                              <option value="video">Video</option>
                              <option value="document">Document</option>
                            </>
                          )}
                        </select>
                      </div>

                      {formData.headerType === "text" && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Header Text
                            <InfoIcon text="Header text (max 60 characters). Must not contain variables or promotional content." />
                          </label>
                          <input
                            type="text"
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                            placeholder="Header text (max 60 chars)"
                            name="headerText"
                            value={formData.headerText}
                            onChange={handleChange}
                            disabled={isReadOnly}
                            maxLength={60}
                          />
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Body Section */}
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Message Body *
                    <InfoIcon text="The main message body. You can use numbered variables like {{1}} for personalization. No variable should appear at the start or end of the message." />
                  </h3>
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2 items-center">
                      <button
                        className={`px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors ${
                          isReadOnly ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                        onClick={() => !isReadOnly && setShowPopup(true)}
                        disabled={isReadOnly}
                      >
                        + Add Variable
                      </button>
                      <InfoIcon text="Insert a variable placeholder like {{1}}, {{2}}. You must provide sample text for each below." />
                      <span className="text-sm text-gray-500">
                        Use &#123;&#123;1&#125;&#125;, &#123;&#123;2&#125;&#125;
                        for personalization
                      </span>
                    </div>

                    <div className="border border-gray-300 rounded-xl overflow-hidden bg-white">
                      <textarea
                        ref={textareaRef}
                        placeholder="Enter your message body here..."
                        className="w-full h-48 px-4 py-3 border-none outline-none resize-none text-gray-700"
                        value={templateBody}
                        onChange={(e) => handleBodyChange(e.target.value)}
                        maxLength={TEMPLATE_BODY_MAX_LENGTH}
                        disabled={isReadOnly}
                      />

                      {errors.templateBody && (
                        <p className="px-4 pt-2 text-red-500 text-xs">
                          {errors.templateBody}
                        </p>
                      )}
                      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-right">
                        <span
                          className={`text-sm ${
                            templateBody.length >= TEMPLATE_BODY_MAX_LENGTH
                              ? "text-red-500"
                              : "text-gray-500"
                          }`}
                        >
                          {templateBody.length}/{TEMPLATE_BODY_MAX_LENGTH} characters
                        </span>
                      </div>
                    </div>

                    {formData.templateCategory === "AUTHENTICATION" && (
                      <section>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          OTP Button Configuration
                          <InfoIcon text="Customize the text for the OTP copy button. This appears in authentication templates." />
                        </h3>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-600 mb-1">
                              OTP Button Text
                            </label>
                            <input
                              type="text"
                              className="w-full rounded-lg p-2 border border-slate-300 focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/40 outline-none transition-all text-sm md:text-base"
                              placeholder="Enter button text (e.g., Copy Code, Copy OTP)"
                              value={otpButtonText}
                              onChange={(e) => setOtpButtonText(e.target.value)}
                              maxLength={20}
                              disabled={isReadOnly}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              This button allows users to copy the OTP code
                            </p>
                          </div>
                        </div>
                      </section>
                    )}
                  </div>
                </section>

                {/* Variable Samples */}
                {(placeholderValues.length > 0 || variableMeta.length > 0) && (
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Variable Samples
                      <InfoIcon text="Provide real examples of variable values for Meta review. Each {{n}} must have a unique sample and realistic value." />
                    </h3>
                    <div className="space-y-3">
                      {Array.from({
                        length: Math.max(
                          placeholderValues.length,
                          variableMeta.length
                        ),
                      }).map((_, i) => {
                        const sample =
                          placeholderValues[i] ?? variableMeta[i]?.sample ?? "";
                        const meta = variableMeta[i] ?? {};
                        return (
                          <div
                            key={i}
                            className="flex flex-col sm:flex-row gap-3 items-start sm:items-center"
                          >
                            <div className="w-20 px-3 py-2 bg-gray-100 rounded-lg text-sm font-mono text-gray-700 shrink-0">
                              {`{{${i + 1}}}`}
                            </div>
                            <input
                              type="text"
                              className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                                sampleErrors[i]
                                  ? "border-red-500"
                                  : "border-gray-300"
                              }`}
                              placeholder={`Sample value for {{${i + 1}}}`}
                              value={sample}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPlaceholderValues((prev) => {
                                  const next = [...prev];
                                  next[i] = value;
                                  return next;
                                });
                                setVariableMeta((prev) => {
                                  const next = [...prev];
                                  next[i] = {
                                    ...(next[i] || {}),
                                    sample: value,
                                  };
                                  return next;
                                });
                              }}
                              disabled={isReadOnly}
                            />
                            <select
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                              value={meta.type || "text"}
                              onChange={(e) => {
                                setVariableMeta((prev) => {
                                  const next = [...prev];
                                  next[i] = {
                                    ...(next[i] || {}),
                                    type: e.target.value,
                                  };
                                  return next;
                                });
                              }}
                              disabled={isReadOnly}
                            >
                              <option value="text">Text</option>
                              <option value="name">Name</option>
                              <option value="number">Number</option>
                            </select>
                          </div>
                        );
                      })}
                    </div>
                  </section>
                )}

                {/* Buttons Section */}
                {showButtons &&
                  formData.templateCategory !== "AUTHENTICATION" && (
                    <section>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Template Buttons
                        <InfoIcon text="Add up to 3 buttons for user actions. Allowed types: Quick Reply (up to 3), URL (1 max), or Phone Number (1 max). Authentication templates automatically include a Copy Code button and do not allow manual buttons." />
                      </h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <select
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            value={newButton.type}
                            onChange={(e) =>
                              setNewButton({
                                ...newButton,
                                type: e.target.value,
                              })
                            }
                            disabled={isReadOnly}
                          >
                            {currentRules.allowedButtonTypes.map((type) => (
                              <option key={type} value={type}>
                                {type === "URL"
                                  ? "URL"
                                  : type === "PHONE_NUMBER"
                                  ? "Call"
                                  : "Quick Reply"}
                              </option>
                            ))}
                          </select>

                          <input
                            type="text"
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="Button text"
                            value={newButton.text}
                            onChange={(e) =>
                              setNewButton({
                                ...newButton,
                                text: e.target.value,
                              })
                            }
                            maxLength={20}
                            disabled={isReadOnly}
                          />

                          {newButton.type === "URL" && (
                            <input
                              type="text"
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              placeholder="URL"
                              value={newButton.url}
                              onChange={(e) =>
                                setNewButton({
                                  ...newButton,
                                  url: e.target.value,
                                })
                              }
                              disabled={isReadOnly}
                            />
                          )}

                          {newButton.type === "PHONE_NUMBER" && (
                            <input
                              type="text"
                              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              placeholder="Phone number"
                              value={newButton.phone}
                              onChange={(e) =>
                                setNewButton({
                                  ...newButton,
                                  phone: e.target.value,
                                })
                              }
                              disabled={isReadOnly}
                            />
                          )}

                          <button
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            onClick={handleAddButton}
                            disabled={
                              !newButton.text ||
                              (newButton.type === "URL" && !newButton.url) ||
                              (newButton.type === "PHONE_NUMBER" &&
                                !newButton.phone) ||
                              isReadOnly
                            }
                          >
                            Add
                          </button>
                        </div>

                        <div className="space-y-2">
                          {templateButtons.map((button, index) => (
                            <div
                              key={index}
                              className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg border border-gray-200"
                            >
                              <div className="flex items-center gap-3">
                                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                                  {button.type}
                                </span>
                                <span className="font-medium text-gray-700">
                                  {button.text}
                                </span>
                                {button.url && (
                                  <span className="text-blue-600 text-sm">
                                    → {button.url}
                                  </span>
                                )}
                                {button.phone && (
                                  <span className="text-green-600 text-sm">
                                    📞 {button.phone}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => handleRemoveButton(index)}
                                className="text-red-500 hover:text-red-700 p-1"
                                disabled={isReadOnly}
                              >
                                <FaTimes />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </section>
                  )}

                {/* Quick Replies */}
                {showQuickReplies && (
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Quick Replies
                      <InfoIcon text="Add up to 3 quick reply buttons. Each should be under 20 characters." />
                    </h3>
                    <div className="space-y-3">
                      <div className="flex flex-col gap-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input
                            type="text"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="Quick reply button text (Question)"
                            value={newQuickReply}
                            onChange={(e) => setNewQuickReply(e.target.value)}
                            maxLength={20}
                            disabled={isReadOnly}
                          />
                          <input
                            type="text"
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                            placeholder="Auto-reply text (Answer)"
                            value={newQuickReplyAnswer}
                            onChange={(e) => setNewQuickReplyAnswer(e.target.value)}
                            disabled={isReadOnly}
                          />
                          <button
                            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                            onClick={handleAddQuickReply}
                            disabled={
                              !newQuickReply.trim() ||
                              quickReplies.length >= 3 ||
                              isReadOnly
                            }
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {quickReplies.map((reply, index) => (
                          <div
                            key={index}
                            className="flex flex-col gap-1 bg-green-50 text-green-900 px-4 py-2 border border-green-200 rounded-lg w-full sm:w-auto min-w-[200px]"
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-semibold text-sm">Btn: {reply.text}</span>
                              <button
                                onClick={() => handleRemoveQuickReply(index)}
                                className="text-red-500 hover:text-red-700"
                                disabled={isReadOnly}
                              >
                                <FaTimes size={12} />
                              </button>
                            </div>
                            {reply.answer && (
                              <span className="text-xs text-gray-500 italic">Reply: {reply.answer}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>
                )}

                {/* Footer */}
                {showFooter && (
                  <section>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Footer
                      <InfoIcon text="Optional short text (max 60 characters). Cannot include variables or promotional content." />
                    </h3>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                      placeholder="Footer text (optional, max 60 characters)"
                      value={templateFooter}
                      onChange={(e) => setTemplateFooter(e.target.value)}
                      maxLength={60}
                      disabled={isReadOnly}
                    />
                  </section>
                )}

                {/* Submit Button */}
                {!isReadOnly && (
                  <div className="pt-6 border-t border-gray-200">
                    <button
                      className="w-full md:w-auto px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      onClick={handleSubmit}
                      disabled={
                        isSubmitting || (formData.type === "MEDIA" && !mediaId)
                      }
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Submitting...
                        </div>
                      ) : (
                        "Submit Template"
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200">
            <div className="p-6">
              <div className="border-b border-gray-200 pb-4 mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Live Preview
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  See how your template will appear to users
                </p>
              </div>
              <div className="flex justify-center">
                <WhatsAppPreview templateData={previewData} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Variable Modal */}
      {showPopup && (
        <div className="fixed inset-0 backdrop-blur-md bg-transparent flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-900">
                  Add Variables
                </h3>
                <button
                  onClick={() => setShowPopup(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <FaTimes className="text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <input
                  type="text"
                  placeholder="Search variables..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  onClick={handleAddAttribute}
                >
                  + New Variable
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Global Variables
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {GLOBAL_ATTRIBUTES.map((attr, index) => (
                      <button
                        key={index}
                        className="px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors"
                        onClick={() => {
                          insertPlaceholderValue(attr);
                          setShowPopup(false);
                        }}
                      >
                        {attr.name}: {attr.value}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">
                    Custom Variables
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {attributes.map((attr, index) => (
                      <button
                        key={index}
                        className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors"
                        onClick={() => {
                          insertPlaceholderValue(attr);
                          setShowPopup(false);
                        }}
                      >
                        {attr.name}: {attr.value}
                        <FaTimes
                          size={12}
                          className="text-red-500 hover:text-red-700 ml-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(index);
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Variable Modal */}
      {showAddAttributePopup && (
        <div className="fixed inset-0 backdrop-blur-md bg-transparent flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                Create New Variable
              </h3>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Variable Name
                </label>
                <input
                  type="text"
                  placeholder="Enter variable name"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={newAttribute.name}
                  onChange={(e) =>
                    setNewAttribute({ ...newAttribute, name: e.target.value })
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sample Value
                </label>
                <input
                  type="text"
                  placeholder="Enter sample value"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  value={newAttribute.value}
                  onChange={(e) =>
                    setNewAttribute({ ...newAttribute, value: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                onClick={handleCloseAddPopup}
              >
                Cancel
              </button>
              <button
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                onClick={handleSaveAttribute}
              >
                Save Variable
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateTemplate;